import React from "react";
import { useSelector } from "react-redux";
import { Tooltip } from "@mui/material";

import makeStyles from "@mui/styles/makeStyles";

import {
  CHOICE_DRIVER_TYPE,
  ROBOT_DRIVER_TYPE,
} from "../../consts/driverTypes";
import { FULL_DAYS } from "../../consts/strings";
import { palette } from "../../theme/palette";

import {
  driverProfileLockSelector,
  driverDayLockSelector,
  getSelectedPlanDay,
  routeDayLockSelector,
} from "../../reducers/matrixProfile/selectors";

import { Text } from "../Base";
import { View } from "../View";
import { LockIcon } from "../Icons";
import RobotDriverMarker from "../RobotDriverMarker";
import ChoiceDriverMarker from "../ChoiceDriverMarker";

const useStyles = makeStyles(() => ({
  container: {
    flex: 1,
    display: "flex",
    gap: "0.5rem",
    marginLeft: ({ hasRouteLockIcon }) => (hasRouteLockIcon ? "1rem" : "0rem"),
  },
  nameContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    height: "1.2rem",
    borderRadius: "0.5rem",
    padding: ({ hasDriverLockIcon }) => {
      if (hasDriverLockIcon) return "0rem 0.5rem";
      else return "0rem";
    },
    backgroundColor: ({ driverProfileLock, driverDayLock }) => {
      if (driverProfileLock?.changed || driverDayLock?.changed)
        return palette.lockOrange;
      else if (
        (driverProfileLock?.lockedByYou &&
          !driverProfileLock.lockedInDifferentBrowser) ||
        (driverDayLock?.lockedByYou && !driverDayLock.lockedInDifferentBrowser)
      )
        return palette.lockGreen;
      else if (
        driverProfileLock?.lockedByUserCode ||
        driverDayLock?.lockedByUserCode
      )
        return palette.lockRed;
      else return palette.transparent;
    },
  },
  name: {
    height: "1.25rem",
    color: "white",
    fontSize: "0.875rem",
    lineHeight: "1.25rem",
    marginTop: ({ hasDriverLockIcon }) =>
      hasDriverLockIcon ? "0.125rem" : "0rem",
  },
  lockIcon: {
    color: ({ driverProfileLock, driverDayLock }) => {
      if (
        driverProfileLock?.lockedByUserCode ||
        driverProfileLock?.changed ||
        driverDayLock?.lockedByUserCode ||
        driverDayLock?.changed
      )
        return palette.typography.black.solid;
      else return palette.transparent;
    },
    width: "10px",
    height: "12px",
    marginRight: "0.35rem",
  },
}));

export const DriverLabel = ({ driver, route }) => {
  const dayOfWeek = useSelector(getSelectedPlanDay);
  const driverProfileLock = useSelector(
    driverProfileLockSelector(driver?.driverCode)
  );
  const driverDayLock = useSelector(driverDayLockSelector(driver?.driverCode));
  const routeDayLock = useSelector(routeDayLockSelector(route.routeCode));
  const hasDriverLockIcon =
    driverProfileLock?.changed ||
    driverDayLock?.changed ||
    driverProfileLock?.lockedByUserCode ||
    driverDayLock?.lockedByUserCode;
  const hasRouteLockIcon =
    routeDayLock?.changed || routeDayLock?.lockedByUserCode;

  const styles = useStyles({
    hasDriverLockIcon,
    driverProfileLock,
    driverDayLock,
    hasRouteLockIcon,
  });

  const getDriverLockTooltipText = () => {
    if (!driverProfileLock && !driverDayLock) return null;
    else if (driverProfileLock.changed)
      return `Driver ${driver.driverCode.split("*")[1]} ${
        driver.driverName
      } profile has been changed.`;
    else if (driverProfileLock.lockedInDifferentBrowser)
      return `Driver ${driver.driverCode.split("*")[1]} ${
        driver.driverName
      } profile is locked by you in different browser/application.`;
    else if (driverProfileLock.lockedByYou)
      return `Driver ${driver.driverCode.split("*")[1]} ${
        driver.driverName
      } profile is locked by you.`;
    else if (driverProfileLock.lockedByUserCode)
      return `Driver ${driver.driverCode.split("*")[1]} ${
        driver.driverName
      } profile is locked by ${driverProfileLock.lockedByUserCode} ${
        driverProfileLock.lockedByUserName
      }.`;
    else if (driverDayLock.changed)
      return `Driver ${driver.driverCode.split("*")[1]} ${driver.driverName} ${
        FULL_DAYS[dayOfWeek]
      } has been changed.`;
    else if (driverDayLock.lockedInDifferentBrowser)
      return `Driver ${driver.driverCode.split("*")[1]} ${driver.driverName} ${
        FULL_DAYS[dayOfWeek]
      } is locked by you in different browser/application.`;
    else if (driverDayLock.lockedByYou)
      return `Driver ${driver.driverCode.split("*")[1]} ${driver.driverName} ${
        FULL_DAYS[dayOfWeek]
      } is locked by you.`;
    else if (driverDayLock.lockedByUserCode)
      return `Driver ${driver.driverCode.split("*")[1]} ${driver.driverName} ${
        FULL_DAYS[dayOfWeek]
      } is locked by ${driverDayLock.lockedByUserCode} ${
        driverDayLock.lockedByUserName
      }.`;
    else return ``;
  };

  return (
    <View className={styles.container}>
      <Tooltip
        title={getDriverLockTooltipText()}
        enterDelay={500}
        enterNextDelay={500}
      >
        <View className={styles.nameContainer}>
          {hasDriverLockIcon && <LockIcon className={styles.lockIcon} />}
          <Text className={styles.name} data-testid="driver-name">
            {driver?.driverName}
          </Text>
        </View>
      </Tooltip>
      {driver?.driverType === CHOICE_DRIVER_TYPE && <ChoiceDriverMarker />}
      {driver?.driverType === ROBOT_DRIVER_TYPE && <RobotDriverMarker />}
    </View>
  );
};

export default DriverLabel;
